import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useSuspenseQuery } from '@tanstack/react-query';
import { reverse } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { unitOtherPartsQuery, unitStructureQuery } from 'api/queries';
import { getOtherPartId } from 'api/responses/models/Unit/UnitOtherPartsDto';
import { AreaName } from 'domain/constants';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { NoDataAvailable } from 'library/components/controls/datagrid/NoDataAvailable';
import { SectionTitleButtons } from 'library/components/controls/layout/SectionTitleButtons';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { reactPlugin } from 'main/azureInsights/config';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { ChangedByInfoDisplay } from 'modules/UnitOverview/Components/ChangedByInfoDisplay';
import { EditProductUnitDataGrid } from 'modules/UnitOverview/Components/EditProductUnitDataGrid';
import { DefaultProductUnitDataGrid } from 'modules/UnitOverview/Components/ProductUnitDataGrid';

const OtherProductsSectionPage = (): React.JSX.Element => {
	const { api } = useProductCenterApi();

	const unitIds = useResolvedUnitIds();

	const { data: unitStructure } = useSuspenseQuery(
		unitStructureQuery(unitIds.id)
	);

	const { data } = useSuspenseQuery({
		...unitOtherPartsQuery(api, unitIds.id),
		select: (data) => ({
			...data,
			unitOtherPartDtos: data.unitOtherPartDtos.map((product) => ({
				id: getOtherPartId(product),
				...product,
			})),
		}),
	});
	const [otherProductEdit, setOtherProductEdit] = useState<boolean>(false);
	const { t } = useTranslation([
		'unitOverview',
		'commonButton',
		'unitCreation',
	]);

	const otherParts = data?.unitOtherPartDtos ?? [];
	const hasData = otherParts && otherParts.length > 0;

	if (otherProductEdit) {
		return (
			<EditProductUnitDataGrid
				otherParts={otherParts}
				unitId={unitIds.id}
				setOtherProductEdit={setOtherProductEdit}
			/>
		);
	}

	return (
		<>
			<SectionTitleButtons>
				<AuthorizeComponent
					areaName={AreaName.Unit}
					permission={OperationType.Write}>
					{unitStructure.isBlueprint ? (
						<Tooltip
							title={t('unitCreation:edit-not-available-text')}>
							<span>
								<Button
									disabled
									variant={'outlined'}
									endIcon={<Edit />}>
									{t('commonButton:edit')}
								</Button>
							</span>
						</Tooltip>
					) : (
						<SectionButton
							text={t('commonButton:edit')}
							icon={<Edit />}
							onClick={() => setOtherProductEdit(true)}
						/>
					)}
				</AuthorizeComponent>
			</SectionTitleButtons>
			{hasData ? (
				<>
					<DefaultProductUnitDataGrid
						otherParts={reverse([...otherParts])}
						isEditMode={false}
					/>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginTop: 3,
						}}>
						<ChangedByInfoDisplay changedByInfo={data?.changedBy} />
					</Box>
				</>
			) : (
				<NoDataAvailable />
			)}
		</>
	);
};

export default withAITracking(
	reactPlugin,
	OtherProductsSectionPage,
	'UnitOverviewOtherProductsSectionPage',
	'application-insight-container'
);
